import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { AntennasVortexTutorialState } from './AntennasTutorialAdapter.js';

type VState = AntennasVortexTutorialState;

const PARTIAL = 'D1D2E,D3C3S,C0D0E';
const PARTIAL_GATED = `${PARTIAL}|E0=B1`;
const SOLVED = 'D1D2E,B2B3W,D3C3S,C0D0E|E0=B1';

const scenes: TutorialScene[] = [
    // Scene 1: Toggle Gates — animate the gate placement flow
    {
        gameState: { antennas: PARTIAL } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'C2', anchor: 'center' },
            { type: 'text-label', text: 'Pair must be linked\nin the Vortex Hub', cell: 'B5', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { antennas: PARTIAL },
                { antennas: PARTIAL, gateMode: true },
                { antennas: PARTIAL_GATED, gateMode: true },
                { antennas: PARTIAL_GATED },
            ] as VState[],
        },
    },

    // Scene 2: Activation — toggle an unblocked pair active; a blocked pair stays amber
    {
        gameState: { antennas: 'B5C5E', gateMode: true, extraGates: [['B5', 'D5']] } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'C1', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Antennas block\ngate activation', cell: 'B5', anchor: 'far-above', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { antennas: 'B5C5E', gateMode: true, extraGates: [['B5', 'D5']] },
                { antennas: 'B5C5E|E0=B1', gateMode: true, extraGates: [['B5', 'D5']] },
            ] as VState[],
        },
    },

    // Scene 3: Signal teleportation
    {
        gameState: { antennas: PARTIAL_GATED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Signal enters\na gate...', cell: 'D0', anchor: 'far-left' },
            { type: 'text-label', text: '...exits from\nthe other side', cell: 'C1', anchor: 'far-right' },
            { type: 'pulse', cell: 'E0', color: COLORS.purple },
            { type: 'pulse', cell: 'B1', color: COLORS.purple },
        ],
    },

    // Scene 4: Full solved state
    {
        gameState: { antennas: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Signals can cross\ngates, not antennas', cell: 'C3', anchor: 'center', color: COLORS.amber },
            { type: 'pulse', cell: 'E0', color: COLORS.purple },
            { type: 'pulse', cell: 'B1', color: COLORS.purple },
        ],
    },
];

/**
 * Vortex Antennas tutorial: 4 scenes on a 6×6 vortex grid with digits 0-3.
 * Teaches signal teleportation through Vortex gates.
 */
export const ANTENNAS_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'antennas-vortex',
    gridCode: '6/0-3:0C4,1C1,2A2,3F3',
    scenes,
    previewScene: 3,
};
